import { useEffect } from "react";
import { AdvancedMarker, Map, useMap } from "@vis.gl/react-google-maps";

/** Reuse the map ID that is already configured in Google Cloud Console */
const MAP_ID = "27cd1eea83179dcb";

/** Default center — Lagos, Nigeria */
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 6.5244, lng: 3.3792 };

function toGoogleLatLng(bound?: LatLng): google.maps.LatLngLiteral | null {
  if (!bound?.latitude || !bound?.longitude) return null;
  const lat = parseFloat(bound.latitude);
  const lng = parseFloat(bound.longitude);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
}

/** Draws a google.maps.Rectangle overlay between NE and SW corners */
function BoundsRectangle({
  ne,
  sw,
}: {
  ne: google.maps.LatLngLiteral;
  sw: google.maps.LatLngLiteral;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const rect = new google.maps.Rectangle({
      bounds: {
        north: Math.max(ne.lat, sw.lat),
        south: Math.min(ne.lat, sw.lat),
        east: Math.max(ne.lng, sw.lng),
        west: Math.min(ne.lng, sw.lng),
      },
      fillColor: "#096DD9",
      fillOpacity: 0.15,
      strokeColor: "#096DD9",
      strokeOpacity: 0.85,
      strokeWeight: 2,
      map,
    });

    return () => {
      rect.setMap(null);
    };
  }, [map, ne.lat, ne.lng, sw.lat, sw.lng]);

  return null;
}

interface ZoneBoundsMapProps {
  northEastBound?: LatLng;
  southWestBound?: LatLng;
  onNEChange: (latLng: LatLng) => void;
  onSWChange: (latLng: LatLng) => void;
}

/** Converts a MapMouseEvent to a LatLng with 6 decimal places */
function eventToLatLng(e: google.maps.MapMouseEvent): LatLng {
  return {
    latitude: e.latLng!.lat().toFixed(6),
    longitude: e.latLng!.lng().toFixed(6),
  };
}

export default function ZoneBoundsMap({
  northEastBound,
  southWestBound,
  onNEChange,
  onSWChange,
}: ZoneBoundsMapProps) {
  const ne = toGoogleLatLng(northEastBound);
  const sw = toGoogleLatLng(southWestBound);

  const center =
    ne && sw
      ? { lat: (ne.lat + sw.lat) / 2, lng: (ne.lng + sw.lng) / 2 }
      : ne ?? sw ?? DEFAULT_CENTER;

  const defaultZoom = ne && sw ? 10 : 11;

  return (
    <div className="w-full h-full rounded-md overflow-hidden border border-gray-200">
      <Map
        defaultCenter={center}
        defaultZoom={defaultZoom}
        mapId={MAP_ID}
        gestureHandling="greedy"
        zoomControl
        disableDefaultUI={false}
      >
        {ne && (
          <AdvancedMarker
            position={ne}
            draggable
            title="North-East bound — drag to adjust"
            onDragEnd={(e: google.maps.MapMouseEvent) => onNEChange(eventToLatLng(e))}
          >
            <div
              style={{
                background: "#096DD9",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 7px",
                borderRadius: 4,
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                cursor: "grab",
                userSelect: "none",
                letterSpacing: "0.5px",
              }}
            >
              NE
            </div>
          </AdvancedMarker>
        )}

        {sw && (
          <AdvancedMarker
            position={sw}
            draggable
            title="South-West bound — drag to adjust"
            onDragEnd={(e: google.maps.MapMouseEvent) => onSWChange(eventToLatLng(e))}
          >
            <div
              style={{
                background: "#F97316",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 7px",
                borderRadius: 4,
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                cursor: "grab",
                userSelect: "none",
                letterSpacing: "0.5px",
              }}
            >
              SW
            </div>
          </AdvancedMarker>
        )}

        {ne && sw && <BoundsRectangle ne={ne} sw={sw} />}
      </Map>
    </div>
  );
}
